import { useContext, useEffect } from "react";
import parse, {
  DOMNode,
  Element,
  HTMLReactParserOptions,
  attributesToProps,
  domToReact,
} from "html-react-parser";

import CommentableModule from "containers/admin/clients/touchpoint/components/comments/commentable-module/commentable-module";
import { DesignPreviewSandbox } from "containers/admin/clients/touchpoint/components/design-preview-sandbox/design-preview-sandbox";
import { EmailHeaders } from "containers/admin/clients/touchpoint/components/email-builder/email-header-preview/email-header-preview";

import CommentContext from "contexts/comment-context";

import { BuilderJsonObject, Module, findModules } from "utilities";
import { BEE_EMAIL_SELECTOR, BLANK_BEE_TEMPLATE } from "utilities/constants";

import { TouchpointPreviewProps } from "types/touchpoint";

interface EmailDesignPreviewProps extends TouchpointPreviewProps {
  emailHeaders?: EmailHeaders;
  hideSubjectAndPreheader?: boolean;
}

function getBeePreviewHead(htmlContent: string, mobileView: boolean) {
  const beeHeadStart = htmlContent.indexOf("<head>") + "<head>".length;
  const beeHeadEnd = htmlContent.indexOf("</head>");
  let beeHead = htmlContent.substring(beeHeadStart, beeHeadEnd);
  beeHead = beeHead.replaceAll("body", BEE_EMAIL_SELECTOR);

  if (mobileView) {
    beeHead = beeHead.replace("@media (max-width:520px){", "").replace("}}", "}");
  }

  return beeHead;
}

function openPreviewLinksInNewTab(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  doc.querySelectorAll("a").forEach((anchor) => {
    const currentTarget = anchor.getAttribute("target");
    if (currentTarget !== "_blank") {
      anchor.setAttribute("target", "_blank");
    }
  });

  return new XMLSerializer().serializeToString(doc);
}

const EmailDesignPreview = ({
  htmlContent,
  htmlJSON,
  isCommentMode = false,
  mobileView = false,
  emailHeaders,
  hideSubjectAndPreheader,
}: EmailDesignPreviewProps) => {
  const parsedJson: BuilderJsonObject = JSON.parse(htmlJSON || BLANK_BEE_TEMPLATE);

  const modules: Module[] = findModules(parsedJson);
  const { commentData } = useContext(CommentContext);

  const beeEmailBodyTag = htmlContent.match(/<body[^>]*>/g)[0];
  const beeEmailBodyTagIndex = htmlContent.indexOf(beeEmailBodyTag) + beeEmailBodyTag.length;
  const beeBodyEnd = htmlContent.indexOf("</body>");
  const beeEmailWrapperOpen = beeEmailBodyTag.replace("body", "div id='bee-email'");
  const beeEmailWrapperClose = "</div>";

  useEffect(() => {
    const beeHead = getBeePreviewHead(htmlContent, mobileView);

    const parser = new DOMParser();
    const doc = parser.parseFromString(beeHead, "text/html");

    // Intentionally inject only inline <style> tags from Bee head.
    // Injecting external <link rel="stylesheet"> into document.head caused global reflow/layout jump.
    // BUT the addition of these links is needed in order to have proper fonts rendered in the preview
    const styleTags = doc.querySelectorAll("style");
    const linkTags = doc.querySelectorAll("link");

    styleTags.forEach((style) => {
      document.head.appendChild(style);
    });
    linkTags.forEach((link) => {
      document.head.appendChild(link);
    });

    return () => {
      styleTags.forEach((style) => {
        if (document.head.contains(style)) {
          document.head.removeChild(style);
        }
      });
      linkTags.forEach((link) => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
    };
  }, [htmlContent, mobileView]);

  const isElement = (domNode: DOMNode): domNode is Element => {
    const isTag = domNode.type === "tag";
    const hasAttributes = (domNode as Element).attribs !== undefined;
    return isTag && hasAttributes;
  };

  /**
   * These are the classNames appended to modules in `EmailBuilderDesignTab` that a user can leave comments on. Spacer modules cannot receive comments. */
  const moduleClassNames = [
    "button_block",
    "heading_block",
    "divider_block",
    "paragraph_block",
    "html_block",
    "icons_block",
    "image_block",
    "menu_block",
    "social_block",
    "text_block",
    "video_block",
    "fromEmail",
    "list_block",
  ];

  let count = 0;

  const CommentableModules: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (
        !isElement(domNode) ||
        (isElement(domNode) && (!domNode.attribs || !domNode.attribs.class))
      ) {
        return;
      }

      const nodeClassNames = domNode.attribs.class.split(" ");

      if (moduleClassNames.some((className) => nodeClassNames.includes(className))) {
        const props = attributesToProps(domNode.attribs);
        const children = domToReact(domNode.children as DOMNode[], CommentableModules);
        const moduleId = modules[count].descriptor.id;
        count++;
        const comment = commentData.items.filter((comment) => comment.annotationId === moduleId)[0];

        return (
          <table className="relative">
            <thead>
              <tr>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-400">
              <tr className="text-sm align-center py-2 pl-0 pr-7 last:pl-7 last:pr-0 last:w-px">
                <td>
                  <table className="relative pointer w-full" {...props}>
                    {children}
                  </table>
                  <CommentableModule moduleId={moduleId} comment={comment} />
                </td>
              </tr>
            </tbody>
          </table>
        );
      }
    },
  };

  const beeEmailContent = htmlContent.substring(beeEmailBodyTagIndex, beeBodyEnd);
  const modifiedHtmlContent = openPreviewLinksInNewTab(beeEmailContent);

  const designPreviewContent = parse(
    beeEmailWrapperOpen + modifiedHtmlContent + beeEmailWrapperClose,
    isCommentMode ? CommentableModules : undefined,
  );

  return (
    <DesignPreviewSandbox
      data-testid="email-builder__preview-sandbox"
      isCommentMode={isCommentMode}
      isMobileView={mobileView}
      emailHeaders={emailHeaders}
      hideSubjectAndPreheader={hideSubjectAndPreheader}>
      {designPreviewContent}
    </DesignPreviewSandbox>
  );
};

export default EmailDesignPreview;
